import React, { useState, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { SxProps, Theme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import useGetReferrersGroup from 'app/features/groups/hooks/useGetReferrersGroup';
import { ReferrersGroups } from 'app/features/groups/types/referrerGroup';
import useSx from './sx';

interface GroupsAndReferrersAutocompleteProps {
  onReferrerSelect: (value: ReferrersGroups | null) => void;
  placeholder: string;
  textFieldProps?: TextFieldProps;
  clearOnSelect?: boolean;
  disableClearable?: boolean;
}

const GroupsAndReferrersAutocomplete: React.FC<GroupsAndReferrersAutocompleteProps> = ({
  onReferrerSelect,
  placeholder,
  textFieldProps,
  clearOnSelect = false,
  disableClearable = false,
}) => {
  const sx = useSx();
  const [inputValue, setInputValue] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<ReferrersGroups | null>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { referrersGroup, isLoading } = useGetReferrersGroup(searchTerm);
  const groups = referrersGroup?.result || [];
  
  const { sx: outerTextfieldSx, ...restTextFieldProps } = textFieldProps || {} as TextFieldProps;
  const combinedSxTextfield = { ...sx.textfield, ...outerTextfieldSx } as SxProps<Theme>;

  // Clear search term when dropdown is opened to show all groups
  useEffect(() => {
    if (open) {
      setSearchTerm('');
    }
  }, [open]);

  const debouncedSearch = useMemo(() => debounce((value: string) => {
    setSearchTerm(value);
  }, 300), []);

  const onInputChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    const value = event.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const onGroupSelectHandler = (_: React.SyntheticEvent<Element, Event>, group: ReferrersGroups | null) => {
    setSelectedGroup(group);
    onReferrerSelect(group);
  
    if (clearOnSelect) {
      setInputValue('');
    }
  };

  return (
    <Autocomplete
      options={groups}
      loading={isLoading}
      getOptionLabel={(option) => option.name || ''}
      onChange={onGroupSelectHandler}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
      value={selectedGroup}
      disableClearable={disableClearable}
      noOptionsText="No hay grupos disponibles"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={combinedSxTextfield}
          onChange={onInputChange}
          {...restTextFieldProps}
        />
      )}
    />
  );
};

export default GroupsAndReferrersAutocomplete;
